import axios from "axios";

// REACT_APP_API_URL may be the backend origin or the /api base.
const rawApiBaseUrl = (process.env.REACT_APP_API_URL || "").trim();
const apiBaseUrl = rawApiBaseUrl
  ? rawApiBaseUrl.replace(/\/+$/, "").endsWith("/api")
    ? rawApiBaseUrl.replace(/\/+$/, "")
    : `${rawApiBaseUrl.replace(/\/+$/, "")}/api`
  : "/api";

const API = axios.create({
  baseURL: apiBaseUrl,
});

export const authHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formConfig = (extra = {}) => ({ headers: authHeader(), ...extra });

export const fetchTrades = async () => (await API.get("/trades")).data;
export const fetchTrade = async (id) => (await API.get(`/trades/${encodeURIComponent(id)}`)).data;
export const createTrade = async (formData, options = {}) =>
  (await API.post("/trades", formData, formConfig(options))).data;
export const updateTrade = async (id, formData, options = {}) =>
  (await API.put(`/trades/${id}`, formData, formConfig(options))).data;
export const deleteTrade = async (id) =>
  (await API.delete(`/trades/${id}`, { headers: authHeader() })).data;

export const fetchFaculties = async () => (await API.get("/faculties")).data;
export const createFaculty = async (formData, options = {}) =>
  (await API.post("/faculties", formData, formConfig(options))).data;
export const updateFaculty = async (id, formData, options = {}) =>
  (await API.put(`/faculties/${id}`, formData, formConfig(options))).data;
export const deleteFaculty = async (id) =>
  (await API.delete(`/faculties/${id}`, { headers: authHeader() })).data;

export const fetchGallery = async () => (await API.get("/gallery")).data;
export const createGalleryItem = async (formData, options = {}) =>
  (await API.post("/gallery", formData, formConfig(options))).data;
export const createGalleryBatch = async (formData, options = {}) =>
  (await API.post("/gallery/batch", formData, formConfig(options))).data;
export const updateGalleryItem = async (id, formData, options = {}) =>
  (await API.put(`/gallery/${id}`, formData, formConfig(options))).data;
export const deleteGalleryItem = async (id) =>
  (await API.delete(`/gallery/${id}`, { headers: authHeader() })).data;

export const fetchSiteSettings = async () => (await API.get("/site")).data;
export const fetchBannerImages = async () => (await API.get("/site/banner")).data;
export const updateBannerImages = async (bannerImages) =>
  (await API.put("/site/banner", { bannerImages }, { headers: authHeader() })).data;
export const addBannerImage = async (formData, options = {}) =>
  (await API.post("/site/banner", formData, formConfig(options))).data;
export const deleteBannerImage = async (index) =>
  (await API.delete(`/site/banner/${index}`, { headers: authHeader() })).data;
export const updateBannerImageAt = async (index, formData, options = {}) =>
  (await API.put(`/site/banner/${index}`, formData, formConfig(options))).data;
export const updateSiteSettings = async (payload) =>
  (await API.put("/site/settings", payload, { headers: authHeader() })).data;
export const updatePrincipalImage = async (formData, options = {}) =>
  (await API.put("/site/principal", formData, formConfig(options))).data;
export const deletePrincipalImage = async () =>
  (await API.delete("/site/principal", { headers: authHeader() })).data;

/**
 * 🔥 FIXED: COMMON ENQUIRY SUBMISSION (USED BY BOTH FORMS)
 */
export const submitEnquiry = async (payload) => {
  const phone = String(payload.phone || "").trim();

  // ✅ STRICT: ONLY 10 DIGITS (NO +, NO SPACES, NO LETTERS)
  if (!/^[0-9]{10}$/.test(phone)) {
    throw new Error("Phone number must be exactly 10 digits");
  }

  try {
    const res = await API.post("/enquiries", payload);
    return res.data;
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to submit enquiry";
    throw new Error(msg);
  }
};

export const fetchEnquiries = async () => (await API.get("/enquiries", { headers: authHeader() })).data;
export const deleteEnquiry = async (id) =>
  (await API.delete(`/enquiries/${id}`, { headers: authHeader() })).data;

export const loginWithGoogle = async (credential) =>
  (await API.post("/auth/google", { credential })).data;
export const loginWithEmail = async (email, password) =>
  (await API.post("/auth/login", { email, password })).data;
export const setupPassword = async (setupToken, password) =>
  (await API.post("/auth/password/setup", { setupToken, password })).data;
export const requestPasswordResetOtp = async (email) =>
  (await API.post("/auth/password/forgot", { email })).data;
export const verifyPasswordResetOtp = async (email, otp) =>
  (await API.post("/auth/password/verify-otp", { email, otp })).data;
export const resetPassword = async (email, resetToken, password) =>
  (await API.post("/auth/password/reset", { email, resetToken, password })).data;

export const fetchTestimonials = async () => (await API.get("/testimonials")).data;
export const createTestimonial = async (formData, options = {}) =>
  (await API.post("/testimonials", formData, formConfig(options))).data;
export const updateTestimonial = async (id, formData, options = {}) =>
  (await API.put(`/testimonials/${id}`, formData, formConfig(options))).data;
export const deleteTestimonial = async (id) =>
  (await API.delete(`/testimonials/${id}`, { headers: authHeader() })).data;


export const fetchAdmissions = async () => (await API.get("/admissions")).data;
export const updateAdmissions = async (formData, options = {}) =>
  (await API.put("/admissions", formData, formConfig(options))).data;
export const deleteAdmissionPdf = async (field = "pdfUrl") =>
  (await API.delete("/admissions/pdf", { params: { field }, headers: authHeader() })).data;

export default API;
